import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  FlaskConical, Brain, Cpu, BookOpen, PieChart, Play, Plus,
  TrendingUp, TrendingDown, Target, Zap, BarChart3, ArrowRight, Loader2
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, CartesianGrid, BarChart, Bar, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const TABS = [
  { id: 'strategy', icon: FlaskConical, labelKey: 'quant.tabStrategy' },
  { id: 'backtest', icon: BarChart3, labelKey: 'quant.tabBacktest' },
  { id: 'swarm', icon: Brain, labelKey: 'quant.tabSwarm' },
  { id: 'knowledge', icon: BookOpen, labelKey: 'quant.tabKnowledge' },
  { id: 'optimizer', icon: PieChart, labelKey: 'quant.tabOptimizer' },
];

const ASSET_CLASSES = ['Equities', 'Crypto', 'FX', 'Commodities'];
const STYLES = ['momentum', 'mean_reversion', 'factor', 'ml_based'];

const chartTooltipStyle = {
  background: '#11141D', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 0, fontFamily: 'monospace', fontSize: 11
};

function MetricBox({ label, value, color = 'emerald' }) {
  const colors = {
    emerald: 'text-emerald-400 border-emerald-500/20',
    blue: 'text-blue-400 border-blue-500/20',
    amber: 'text-amber-400 border-amber-500/20',
    cyan: 'text-cyan-400 border-cyan-500/20',
    red: 'text-red-400 border-red-500/20',
    purple: 'text-purple-400 border-purple-500/20',
  };
  const c = colors[color] || colors.emerald;
  return (
    <div className={`bg-[#090B0F] border ${c.split(' ')[1]} p-3`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</p>
      <p className={`text-lg font-black font-mono ${c.split(' ')[0]}`}>{value}</p>
    </div>
  );
}

/* ─── STRATEGY BUILDER ─── */
function StrategyBuilder({ t }) {
  const [form, setForm] = useState({ name: '', asset_class: 'Equities', style: 'momentum', max_drawdown: 15, min_sharpe: 1.0 });
  const [strategies, setStrategies] = useState([]);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.post('/api/quant-research/strategy', {
        name: form.name || undefined,
        asset_class: form.asset_class,
        style: form.style,
        constraints: { max_drawdown: +form.max_drawdown, min_sharpe: +form.min_sharpe },
      });
      setStrategies(prev => [res.data, ...prev]);
      setSelected(res.data);
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const loadAll = useCallback(async () => {
    try {
      const res = await api.get('/api/quant-research/strategies');
      setStrategies(res.data.strategies || []);
    } catch (e) {}
  }, []);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Form */}
      <div className="bg-[#090B0F] border border-white/[0.06] p-5 lg:col-span-1">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-emerald-400" /> {t('quant.createStrategy')}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.strategyName')}</label>
            <input className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 focus:border-emerald-500/50 outline-none"
              placeholder="Alpha Momentum v2" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="strategy-name-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.assetClass')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.asset_class} onChange={e => setForm(f => ({ ...f, asset_class: e.target.value }))} data-testid="strategy-asset-select">
              {ASSET_CLASSES.map(ac => <option key={ac} value={ac}>{ac}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.style')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.style} onChange={e => setForm(f => ({ ...f, style: e.target.value }))} data-testid="strategy-style-select">
              {STYLES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.maxDrawdown')}</label>
              <input type="number" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
                value={form.max_drawdown} onChange={e => setForm(f => ({ ...f, max_drawdown: e.target.value }))} data-testid="strategy-maxdd-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.minSharpe')}</label>
              <input type="number" step="0.1" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
                value={form.min_sharpe} onChange={e => setForm(f => ({ ...f, min_sharpe: e.target.value }))} data-testid="strategy-sharpe-input" />
            </div>
          </div>
          <button onClick={create} disabled={creating}
            className="w-full py-2.5 bg-emerald-500 text-[#030508] font-bold text-xs tracking-wider hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="create-strategy-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {creating ? t('quant.generating') : t('quant.generateStrategy')}
          </button>
        </div>
      </div>

      {/* Strategy details / list */}
      <div className="lg:col-span-2 space-y-4">
        {selected && (
          <div className="bg-[#090B0F] border border-emerald-500/20 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-base">{selected.name}</h3>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{selected.asset_class} / {selected.style}</p>
              </div>
              <span className="text-[10px] px-2 py-1 bg-emerald-500/10 text-emerald-400 font-bold uppercase">{selected.status}</span>
            </div>
            <p className="text-sm text-slate-400 mb-4 leading-relaxed">{selected.thesis}</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
              <MetricBox label={t('quant.sharpe')} value={selected.metrics?.sharpe} color="emerald" />
              <MetricBox label={t('quant.sortino')} value={selected.metrics?.sortino} color="blue" />
              <MetricBox label={t('quant.return')} value={`${selected.metrics?.total_return}%`} color="cyan" />
              <MetricBox label={t('quant.annualReturn')} value={`${selected.metrics?.annual_return}%`} color="purple" />
              <MetricBox label={t('quant.maxDrawdown')} value={`${selected.metrics?.max_drawdown}%`} color="red" />
              <MetricBox label={t('quant.volatility')} value={`${selected.metrics?.volatility}%`} color="amber" />
            </div>
            {/* Signals table */}
            <h4 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t('quant.signals')}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-white/[0.06]">
                    <th className="text-left py-2 px-2">{t('quant.symbol')}</th>
                    <th className="text-left py-2 px-2">{t('quant.direction')}</th>
                    <th className="text-right py-2 px-2">{t('quant.confidence')}</th>
                    <th className="text-right py-2 px-2">{t('quant.weight')}</th>
                    <th className="text-right py-2 px-2">SL%</th>
                    <th className="text-right py-2 px-2">TP%</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.signals || []).map((s, i) => (
                    <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2 px-2 text-white font-mono font-bold">{s.symbol}</td>
                      <td className={`py-2 px-2 font-bold ${s.direction === 'LONG' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {s.direction === 'LONG' ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                        {s.direction}
                      </td>
                      <td className="py-2 px-2 text-right text-slate-300 font-mono">{(s.confidence * 100).toFixed(0)}%</td>
                      <td className="py-2 px-2 text-right text-slate-300 font-mono">{(s.weight * 100).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right text-red-400 font-mono">{s.stop_loss_pct}%</td>
                      <td className="py-2 px-2 text-right text-emerald-400 font-mono">{s.take_profit_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Strategy list */}
        <div className="bg-[#090B0F] border border-white/[0.06] p-4">
          <h3 className="text-sm font-bold text-white mb-3">{t('quant.yourStrategies')} ({strategies.length})</h3>
          {strategies.length === 0 ? (
            <p className="text-slate-500 text-sm">{t('quant.noStrategies')}</p>
          ) : (
            <div className="space-y-1">
              {strategies.map(s => (
                <button key={s.strategy_id} onClick={() => setSelected(s)}
                  className={`w-full text-left flex items-center justify-between px-3 py-2.5 transition-colors ${selected?.strategy_id === s.strategy_id ? 'bg-emerald-500/10 border-l-2 border-emerald-500' : 'hover:bg-white/[0.03] border-l-2 border-transparent'}`}
                  data-testid={`strategy-item-${s.strategy_id}`}>
                  <div>
                    <span className="text-sm text-white font-medium">{s.name}</span>
                    <span className="text-[10px] text-slate-500 ml-2 font-mono">{s.asset_class} / {s.style}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-emerald-400">SR {s.metrics?.sharpe}</span>
                    <span className={`text-xs font-mono ${s.metrics?.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{s.metrics?.total_return}%</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── BACKTEST ENGINE ─── */
function BacktestEngine({ t }) {
  const [form, setForm] = useState({ strategy_id: '', start_date: '2024-01-01', end_date: '2025-01-01', initial_capital: 100000 });
  const [strategies, setStrategies] = useState([]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  React.useEffect(() => {
    api.get('/api/quant-research/strategies').then(r => {
      const strats = r.data.strategies || [];
      setStrategies(strats);
      if (strats.length > 0) setForm(f => ({ ...f, strategy_id: strats[0].strategy_id }));
    }).catch(() => {});
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post('/api/quant-research/backtest', form);
      setResult(res.data);
    } catch (e) { console.error(e); }
    setRunning(false);
  };

  const equityCurve = result?.results?.equity_curve?.map((v, i) => ({ day: i, value: v })) || [];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Play className="w-4 h-4 text-blue-400" /> {t('quant.runBacktest')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.strategy')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.strategy_id} onChange={e => setForm(f => ({ ...f, strategy_id: e.target.value }))} data-testid="backtest-strategy-select">
              <option value="">{t('quant.noStrategy')}</option>
              {strategies.map(s => <option key={s.strategy_id} value={s.strategy_id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.startDate')}</label>
            <input type="date" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="backtest-start-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.endDate')}</label>
            <input type="date" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="backtest-end-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.capital')}</label>
            <input type="number" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.initial_capital} onChange={e => setForm(f => ({ ...f, initial_capital: +e.target.value }))} data-testid="backtest-capital-input" />
          </div>
        </div>
        <button onClick={run} disabled={running}
          className="mt-4 px-6 py-2.5 bg-blue-500 text-white font-bold text-xs tracking-wider hover:bg-blue-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="run-backtest-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? t('quant.backtesting') : t('quant.runBacktest')}
        </button>
      </div>

      {/* Results */}
      {result && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <MetricBox label={t('quant.sharpe')} value={result.results?.sharpe} color="emerald" />
            <MetricBox label={t('quant.sortino')} value={result.results?.sortino} color="blue" />
            <MetricBox label={t('quant.return')} value={`${result.results?.total_return}%`} color="cyan" />
            <MetricBox label={t('quant.winRate')} value={`${result.results?.win_rate}%`} color="purple" />
            <MetricBox label={t('quant.maxDrawdown')} value={`${result.results?.max_drawdown}%`} color="red" />
            <MetricBox label={t('quant.trades')} value={result.results?.trades_count} color="amber" />
          </div>

          {/* Equity Curve */}
          <div className="bg-[#090B0F] border border-white/[0.06] p-5">
            <h3 className="text-sm font-bold text-white mb-3">{t('quant.equityCurve')}</h3>
            <div className="h-[300px]" data-testid="backtest-equity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={equityCurve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={v => [`$${v.toLocaleString()}`, 'Value']} />
                  <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Trades table */}
          <div className="bg-[#090B0F] border border-white/[0.06] p-4">
            <h3 className="text-sm font-bold text-white mb-3">{t('quant.recentTrades')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-white/[0.06]">
                    <th className="text-left py-2 px-2">{t('quant.day')}</th>
                    <th className="text-left py-2 px-2">{t('quant.symbol')}</th>
                    <th className="text-left py-2 px-2">{t('quant.side')}</th>
                    <th className="text-right py-2 px-2">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.trades || []).slice(0, 20).map((tr, i) => (
                    <tr key={i} className="border-b border-white/[0.03]">
                      <td className="py-1.5 px-2 text-slate-400 font-mono">{tr.day}</td>
                      <td className="py-1.5 px-2 text-white font-mono">{tr.symbol}</td>
                      <td className={`py-1.5 px-2 font-bold ${tr.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>{tr.side}</td>
                      <td className={`py-1.5 px-2 text-right font-mono ${tr.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tr.pnl >= 0 ? '+' : ''}{tr.pnl.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── AGENT SWARM ─── */
function AgentSwarm({ t }) {
  const [form, setForm] = useState({ asset_class: 'Equities', num_agents: 8 });
  const [result, setResult] = useState(null);
  const [launching, setLaunching] = useState(false);

  const launch = async () => {
    setLaunching(true);
    try {
      const res = await api.post('/api/quant-research/agent-swarm', form);
      setResult(res.data);
    } catch (e) { console.error(e); }
    setLaunching(false);
  };

  const radarData = result?.all_experiments?.map(e => ({
    agent: e.agent_id,
    sharpe: e.metrics.sharpe,
    sortino: Math.min(e.metrics.sortino, 5),
    return: Math.min(e.metrics.total_return, 50),
  })) || [];

  return (
    <div className="space-y-4">
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-400" /> {t('quant.launchSwarm')}
        </h3>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.assetClass')}</label>
            <select className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.asset_class} onChange={e => setForm(f => ({ ...f, asset_class: e.target.value }))} data-testid="swarm-asset-select">
              {ASSET_CLASSES.map(ac => <option key={ac} value={ac}>{ac}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.numAgents')}</label>
            <input type="number" min={2} max={12} className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.num_agents} onChange={e => setForm(f => ({ ...f, num_agents: +e.target.value }))} data-testid="swarm-num-input" />
          </div>
        </div>
        <button onClick={launch} disabled={launching}
          className="mt-4 px-6 py-2.5 bg-purple-500 text-white font-bold text-xs tracking-wider hover:bg-purple-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="launch-swarm-btn">
          {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
          {launching ? t('quant.launching') : t('quant.launchSwarm')}
        </button>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricBox label={t('quant.experimentsRun')} value={result.experiments_run} color="purple" />
            <MetricBox label={t('quant.uniqueStrats')} value={result.unique_strategies} color="blue" />
            <MetricBox label={t('quant.avgSharpe')} value={result.avg_sharpe} color="emerald" />
            <MetricBox label={t('quant.bestSharpe')} value={result.best_performers?.[0]?.metrics?.sharpe || '-'} color="cyan" />
          </div>

          {/* Ensemble recommendation */}
          {result.ensemble && (
            <div className="bg-[#090B0F] border border-purple-500/20 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-bold text-white">{t('quant.ensembleRec')}</h3>
              </div>
              <p className="text-xs text-slate-400 mb-3">{result.ensemble.recommendation}</p>
              <div className="grid grid-cols-3 gap-2">
                <MetricBox label={t('quant.sharpe')} value={result.ensemble.sharpe} color="emerald" />
                <MetricBox label={t('quant.return')} value={`${result.ensemble.expected_return}%`} color="blue" />
                <MetricBox label={t('quant.maxDrawdown')} value={`${result.ensemble.max_drawdown}%`} color="red" />
              </div>
              {result.convergence && (
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-4 text-[10px] text-slate-500">
                  <span>{t('quant.dominantStyle')}: <span className="text-white font-mono">{result.convergence.dominant_style}</span></span>
                  <span>{t('quant.sharpeStd')}: <span className="text-white font-mono">{result.convergence.sharpe_std}</span></span>
                  <span>{t('quant.consensus')}: <span className={result.convergence.top3_agree_direction ? 'text-emerald-400' : 'text-amber-400'}>{result.convergence.top3_agree_direction ? 'YES' : 'NO'}</span></span>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Radar chart */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-5">
              <h3 className="text-sm font-bold text-white mb-3">{t('quant.agentComparison')}</h3>
              <div className="h-[280px]" data-testid="swarm-radar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis dataKey="agent" tick={{ fill: '#94a3b8', fontSize: 9 }} />
                    <PolarRadiusAxis tick={{ fill: '#64748b', fontSize: 9 }} />
                    <Radar dataKey="sharpe" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                    <Radar dataKey="return" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Experiment results table */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-4">
              <h3 className="text-sm font-bold text-white mb-3">{t('quant.allExperiments')}</h3>
              <div className="overflow-y-auto max-h-[300px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#090B0F]">
                    <tr className="text-slate-500 border-b border-white/[0.06]">
                      <th className="text-left py-2 px-2">Agent</th>
                      <th className="text-left py-2 px-2">{t('quant.hypothesis')}</th>
                      <th className="text-right py-2 px-2">{t('quant.sharpe')}</th>
                      <th className="text-right py-2 px-2">{t('quant.return')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.all_experiments || []).map((e, i) => (
                      <tr key={i} className={`border-b border-white/[0.03] ${i < 3 ? 'bg-emerald-500/5' : ''}`}>
                        <td className="py-2 px-2 text-white font-mono font-bold text-[10px]">{e.agent_id}</td>
                        <td className="py-2 px-2 text-slate-400 max-w-[180px] truncate">{e.hypothesis}</td>
                        <td className="py-2 px-2 text-right text-emerald-400 font-mono">{e.metrics.sharpe}</td>
                        <td className={`py-2 px-2 text-right font-mono ${e.metrics.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {e.metrics.total_return}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── KNOWLEDGE BASE ─── */
function KnowledgeBase({ t }) {
  const [findings, setFindings] = useState([]);
  const [filter, setFilter] = useState('All');

  React.useEffect(() => {
    api.get('/api/quant-research/knowledge-base').then(r => setFindings(r.data.findings || [])).catch(() => {});
  }, []);

  const categories = ['All', ...new Set(findings.map(f => f.category))];
  const filtered = filter === 'All' ? findings : findings.filter(f => f.category === filter);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-3 py-1.5 text-xs font-bold tracking-wider transition-colors ${filter === c ? 'bg-emerald-500 text-[#030508]' : 'bg-[#090B0F] text-slate-400 hover:text-white border border-white/[0.06]'}`}
            data-testid={`kb-filter-${c}`}>{c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(f => (
          <div key={f.id} className="bg-[#090B0F] border border-white/[0.06] p-4 hover:border-emerald-500/20 transition-colors" data-testid={`kb-card-${f.id}`}>
            <div className="flex items-start justify-between mb-2">
              <span className="text-[10px] font-mono text-slate-500">{f.id}</span>
              <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-400 font-bold">{f.regime}</span>
            </div>
            <h4 className="text-sm font-bold text-white mb-2">{f.title}</h4>
            <p className="text-xs text-slate-400 leading-relaxed mb-3">{f.finding}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-500">{f.source}</span>
              <div className="flex items-center gap-1">
                <Target className="w-3 h-3 text-emerald-400" />
                <span className="text-xs font-mono text-emerald-400">{(f.confidence * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── PORTFOLIO OPTIMIZER ─── */
function PortfolioOptimizer({ t }) {
  const [strategies, setStrategies] = useState([]);
  const [form, setForm] = useState({ target_sharpe: 2.0, max_correlation: 0.5 });
  const [result, setResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  React.useEffect(() => {
    api.get('/api/quant-research/strategies').then(r => setStrategies(r.data.strategies || [])).catch(() => {});
  }, []);

  const optimize = async () => {
    setOptimizing(true);
    try {
      const res = await api.post('/api/quant-research/optimize', {
        strategies: strategies.map(s => s.strategy_id),
        target_sharpe: +form.target_sharpe,
        max_correlation: +form.max_correlation,
      });
      setResult(res.data);
    } catch (e) { console.error(e); }
    setOptimizing(false);
  };

  const frontierData = result?.efficient_frontier || [];
  const weightsData = result?.optimal_weights?.map(w => ({ name: w.strategy, weight: +(w.weight * 100).toFixed(1), sharpe: w.sharpe })) || [];

  return (
    <div className="space-y-4">
      <div className="bg-[#090B0F] border border-white/[0.06] p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <PieChart className="w-4 h-4 text-cyan-400" /> {t('quant.optimizePortfolio')}
        </h3>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.targetSharpe')}</label>
            <input type="number" step="0.1" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.target_sharpe} onChange={e => setForm(f => ({ ...f, target_sharpe: e.target.value }))} data-testid="optimizer-sharpe-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{t('quant.maxCorrelation')}</label>
            <input type="number" step="0.1" min="0" max="1" className="w-full bg-[#0D1017] border border-white/10 text-white text-sm px-3 py-2 outline-none"
              value={form.max_correlation} onChange={e => setForm(f => ({ ...f, max_correlation: e.target.value }))} data-testid="optimizer-corr-input" />
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">{strategies.length} {t('quant.strategiesAvailable')}</p>
        <button onClick={optimize} disabled={optimizing}
          className="mt-3 px-6 py-2.5 bg-cyan-500 text-[#030508] font-bold text-xs tracking-wider hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="optimize-btn">
          {optimizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PieChart className="w-4 h-4" />}
          {optimizing ? t('quant.optimizing') : t('quant.runOptimization')}
        </button>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricBox label={t('quant.portfolioSharpe')} value={result.portfolio_sharpe} color="emerald" />
            <MetricBox label={t('quant.portfolioSortino')} value={result.portfolio_sortino} color="blue" />
            <MetricBox label={t('quant.expectedReturn')} value={`${result.expected_return}%`} color="cyan" />
            <MetricBox label={t('quant.portfolioVol')} value={`${result.portfolio_volatility}%`} color="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Weights bar chart */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-5">
              <h3 className="text-sm font-bold text-white mb-3">{t('quant.optimalWeights')}</h3>
              <div className="h-[250px]" data-testid="optimizer-weights-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weightsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={v => [`${v}%`, 'Weight']} />
                    <Bar dataKey="weight" fill="#10b981" radius={[0, 2, 2, 0]}>
                      {weightsData.map((_, i) => (
                        <Cell key={i} fill={['#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444'][i % 6]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Efficient Frontier scatter */}
            <div className="bg-[#090B0F] border border-white/[0.06] p-5">
              <h3 className="text-sm font-bold text-white mb-3">{t('quant.efficientFrontier')}</h3>
              <div className="h-[250px]" data-testid="optimizer-frontier-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="risk" name="Risk" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <YAxis dataKey="return" name="Return" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(v, name) => [`${v}%`, name]} />
                    <Scatter data={frontierData} fill="#06b6d4" line={{ stroke: '#06b6d4', strokeWidth: 2 }} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function QuantResearchLab() {
  const [tab, setTab] = useState('strategy');
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#030508] text-white p-4 sm:p-6" data-testid="quant-research-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <FlaskConical className="w-6 h-6 text-emerald-400" />
          <h1 className="text-xl sm:text-2xl font-black tracking-tight">{t('quant.title')}</h1>
        </div>
        <p className="text-sm text-slate-500 ml-9">{t('quant.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1 border-b border-white/[0.06]">
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider whitespace-nowrap transition-colors ${
              tab === tb.id ? 'text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/5' : 'text-slate-500 hover:text-white'
            }`}
            data-testid={`quant-tab-${tb.id}`}>
            <tb.icon className="w-4 h-4" />
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'strategy' && <StrategyBuilder t={t} />}
      {tab === 'backtest' && <BacktestEngine t={t} />}
      {tab === 'swarm' && <AgentSwarm t={t} />}
      {tab === 'knowledge' && <KnowledgeBase t={t} />}
      {tab === 'optimizer' && <PortfolioOptimizer t={t} />}
    </div>
  );
}
